import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  ChatViewer,
  ChatViewerHandle,
  followMessagesAtBottom,
  followEveryMessage,
} from 'chat-viewer';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  MessageType,
  Sender,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import { messagesModel, addMessage, selectMessages } from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { A11Y_CHAT_DIALOG_ID } from '@/constants';

import { useOnEditorSettingsEvent } from 'AiAssistantWidget/Widget/contexts';
import { settingsEvents } from 'AiAssistantWidget/settingsEvents';

import { Container } from '../Container';
import { Header } from '../Header';
import { Input } from '../Input';
import { ChatMessage } from '../ChatMessage';
import { Loader } from '../Loader';

import { st, classes, cssStates } from './Chat.st.css';
import { useHistoryLoader } from './useHistoryLoader';
import { useIntroSequence } from './useIntroSequence';

export const Chat: React.FC = () => {
  const { t } = useTranslation();
  const chatRef = useRef<ChatViewerHandle<messagesModel.Message>>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages.visibleMessages);
  const isTyping = useAppSelector(selectMessages.isTyping);
  const isLoaded = useAppSelector(selectMessages.isLoaded);
  const isLoading = useAppSelector(selectMessages.isLoading);
  const loadingFirstPage = useAppSelector(selectMessages.loadingFirstPage);
  const messagesIdle = useAppSelector(selectMessages.isIdle);
  const loadOlderMessages = useHistoryLoader();
  const { isEditor } = useEnvironment();

  const isBusy = useMemo(
    () => isTyping || !messagesIdle,
    [isTyping, messagesIdle],
  );

  const { isSequenceFulfilled } = useIntroSequence();

  const [value, setValue] = React.useState('');

  const onInputChange = useCallback((newValue: string) => {
    setValue(newValue);
    chatRef.current?.scrollToIndex(-1, { align: 'end', smooth: true });
  }, []);

  const onSubmit = useCallback(() => {
    if (value.trim().length) {
      dispatch(addMessage({ body: { mainText: value } }));
      setValue('');
    }
  }, [dispatch, value]);

  useOnEditorSettingsEvent(
    settingsEvents.openAssistantMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      chatRef.current?.scrollToIndex(1, { align: 'start', smooth: true });
    }, [isEditor]),
  );

  useEffect(() => {
    if (isEditor) {
      chatRef.current?.scrollToIndex(0, { align: 'end', smooth: true });
    }
  }, [isEditor, messages]);

  const renderMessage = useCallback(
    (message: messagesModel.Message) => {
      return (
        <ChatMessage
          key={message.id}
          message={message}
          last={message.id === messages.at(-1)?.id}
        />
      );
    },
    [messages],
  );

  const onNewerMessages = useCallback(
    (
      chat: ChatViewerHandle<messagesModel.Message>,
      received: messagesModel.Message[],
    ) => {
      if (isEditor) {
        return chat.scrollToTop({ align: 'end', smooth: true });
      }

      const lastMessage = received.at(-1);
      const isContactForm =
        lastMessage?.messageType === MessageType.CONTACT_FORM;

      if (isContactForm) {
        return chat.scrollToId(lastMessage.id, { align: 'start' });
      }

      const hasUserMessages = received.some(
        (message) => message.sender === Sender.SITE_VISITOR,
      );

      if (hasUserMessages) {
        return followEveryMessage<messagesModel.Message>()(chat, received);
      }

      return followMessagesAtBottom<messagesModel.Message>()(chat, received);
    },
    [isEditor],
  );

  return (
    <Container ariaBusy={isBusy} dialogId={A11Y_CHAT_DIALOG_ID}>
      <Header dialogId={A11Y_CHAT_DIALOG_ID} />
      <div ref={parentRef} className={st(classes.container)}>
        {loadingFirstPage && <Loader />}
        <div
          className={st(classes.wrapper, cssStates({ isVisible: isLoaded }))}
          role="log"
          aria-label={t('app.widget.ariallabel.chatLog')}
          tabIndex={0}
        >
          <ChatViewer<messagesModel.Message>
            ref={chatRef}
            alignment="top"
            className={classes.chatViewer}
            scrollerClassName={classes.scroller}
            overscan={10}
            historyEndOffset={5}
            onHistoryEndReached={loadOlderMessages}
            prefix={isLoading && !isEditor && <Loader />}
            suffix={isTyping && !isEditor && <Loader />}
            messages={messages}
            renderMessage={renderMessage}
            onNewerMessages={onNewerMessages}
          />
        </div>
      </div>
      <Input
        value={value}
        onChange={onInputChange}
        onSubmit={onSubmit}
        disabled={!isSequenceFulfilled}
      />
    </Container>
  );
};
